import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import SupportThread from '@/components/global-admin/SupportThread';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type Target = { organizationId: string; name: string };

type SupportChatContextValue = {
  openSupport: (organizationId: string, name: string) => void;
};

const SupportChatContext = React.createContext<SupportChatContextValue | null>(null);

export function useSupportChat() {
  const value = React.useContext(SupportChatContext);
  if (!value) throw new Error('useSupportChat needs a SupportChatProvider');
  return value;
}

export default function SupportChatProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = React.useState<Target | null>(null);
  const [conversationId, setConversationId] = React.useState<string | null>(null);

  const openSupport = React.useCallback(async (organizationId: string, name: string) => {
    setTarget({ organizationId, name });
    setConversationId(null);
    try {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post(`/global-admin/support/organizations/${organizationId}/conversation/`);
      setConversationId(res.data.id);
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message);
      setTarget(null);
    }
  }, []);

  const value = React.useMemo(() => ({ openSupport }), [openSupport]);

  return (
    <SupportChatContext.Provider value={value}>
      {children}
      <Sheet open={target !== null} onOpenChange={(open) => { if (!open) setTarget(null); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" data-testid="support-sidebar">
          <SheetHeader className="border-b px-4 py-3 text-left">
            <SheetTitle className="text-base">{target?.name}</SheetTitle>
            <SheetDescription>Your conversation with them as BitSwan support. They read it in their portal.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1">
            {conversationId ? (
              <SupportThread key={conversationId} conversationId={conversationId} />
            ) : (
              <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </SupportChatContext.Provider>
  );
}
